import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, CardMedia, Typography, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { API_URL } from '../config';
import { parsePriceString } from '../utils/currency';

const ProductCard = ({ product, onToggleSave, savedItems = [], boardList, currentBoardId, onMove, onRemove, formatPrice }) => {
    const isSaved = savedItems.some(item => item.link === product.link);
    const fakeDelivery = product.deliveryDate || '09/06-15/06';
    const imageUrl = product.imageUrl || product.image || 'https://via.placeholder.com/120x120?text=No+Image';
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Debug logging for extracted links
    if (product.extractedProductLink && process.env.NODE_ENV !== 'production') {
        console.log(`🔗 ProductCard "${product.title}" has extracted link: ${product.extractedProductLink}`);
    }

    const numericPrice = typeof product.numericPrice === 'number'
        ? product.numericPrice
        : parsePriceString(product.price);
    const displayPrice = formatPrice && numericPrice !== null && numericPrice !== undefined
        ? formatPrice(numericPrice)
        : (product.price || 'N/A');

    const [open, setOpen] = useState(false);
    const [selectedBoard, setSelectedBoard] = useState('');
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [boards, setBoards] = useState([]);
    const [creatingNew, setCreatingNew] = useState(false);
    const [newBoardName, setNewBoardName] = useState('');
    const [newBoardDesc, setNewBoardDesc] = useState('');
    const [moveBoard, setMoveBoard] = useState(currentBoardId || '');

    useEffect(() => {
        if (open) {
            const fetchBoards = async () => {
                try {
                    const token = localStorage.getItem('token');
                    if (!token) return;
                    const response = await axios.get(`${API_URL}/api/boards`, {
                        headers: { 'x-auth-token': token }
                    });
                    setBoards(response.data || []);
                } catch (err) {
                    setBoards([]);
                }
            };
            fetchBoards();
        }
    }, [open]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleBoardSelect = (event) => {
        const value = event.target.value;
        setSelectedBoard(value);
        setCreatingNew(value === '__new__');
    };

    const handleAddToBoard = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');
            let boardId = selectedBoard;
            if (creatingNew) {
                // Create the new board
                const createRes = await axios.post(
                    `${API_URL}/api/boards`,
                    { name: newBoardName, description: newBoardDesc },
                    { headers: { 'x-auth-token': token } }
                );
                boardId = createRes.data._id;
            }
            // Add the product reference to the board
            if (process.env.NODE_ENV !== 'production') {
                console.log('Saving product to board (ref):', product.link);
            }
            await axios.post(
                `${API_URL}/api/boards/${boardId}/items`,
                { type: 'product', ref: product.link },
                { headers: { 'x-auth-token': token } }
            );
            setFeedback('Product added successfully!');
            onToggleSave && onToggleSave(product);
        } catch (error) {
            setFeedback('An error occurred.');
        } finally {
            setLoading(false);
            handleClose();
            setCreatingNew(false);
            setNewBoardName('');
            setNewBoardDesc('');
        }
    };

    const handleMoveBoard = (e) => {
        const newBoardId = e.target.value;
        setMoveBoard(newBoardId);
        if (process.env.NODE_ENV !== 'production') {
            console.log('Moving product', product, 'from', currentBoardId, 'to', newBoardId);
        }
        if (onMove) onMove(product, currentBoardId, newBoardId);
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 3 },
                borderRadius: { xs: 2, sm: 4 },
                boxShadow: {
                    xs: '0 6px 18px rgba(15,23,42,0.12)',
                    sm: 2,
                },
                mb: { xs: 2, sm: 3 },
                p: { xs: 1.5, sm: 3 },
                alignItems: { xs: 'stretch', sm: 'center' },
                width: '100%',
                maxWidth: 900,
                mx: 'auto',
                minHeight: { xs: 'auto', sm: 160 },
                position: 'relative',
            }}
        >
            {onRemove && (
                <Button
                    onClick={() => onRemove(product)}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        minWidth: 0,
                        padding: 0.5,
                        color: 'error.main',
                    }}
                    size="small"
                >
                    <DeleteIcon fontSize="small" />
                </Button>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={product.title || 'Product'}
                    sx={{
                        width: { xs: 72, sm: 120 },
                        height: { xs: 72, sm: 120 },
                        borderRadius: 3,
                        objectFit: 'cover',
                        boxShadow: 1,
                        mr: { xs: 0, sm: 3 },
                        background: '#f5f5f5',
                    }}
                />
            </Box>
            <CardContent
                sx={{
                    flex: 1,
                    p: 0,
                    pl: { xs: 0, sm: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: { xs: 0.5, sm: 0.75 },
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        lineHeight: 1.25,
                        fontSize: { xs: '0.95rem', sm: '1.15rem' },
                    }}
                >
                    {product.title || 'Product Name'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.78rem', sm: '0.9rem' } }}>
                        {product.source || 'Store Name'}
                    </Typography>
                </Box>
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={isMobile ? 0.75 : 3}
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    sx={{ mb: isMobile ? 0 : 1 }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.78rem', sm: '0.9rem' } }}>
                        <b>Earliest Delivery</b> <br /> {fakeDelivery}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.78rem', sm: '0.9rem' } }}>
                        <b>Rating</b> <br /> {product.rating ? `${product.rating}/5.0` : 'N/A'}
                    </Typography>
                    {boardList && onMove ? (
                        <Select
                            value={moveBoard}
                            onChange={handleMoveBoard}
                            size="small"
                            sx={{ minWidth: isMobile ? '100%' : 160, ml: isMobile ? 0 : 2 }}
                        >
                            {boardList.map(board => (
                                <MenuItem key={board._id} value={board._id} disabled={board._id === currentBoardId}>
                                    {board.name}
                                </MenuItem>
                            ))}
                        </Select>
                    ) : (
                        <Button
                            variant={isSaved ? 'contained' : 'outlined'}
                            color={isSaved ? 'primary' : 'inherit'}
                            startIcon={isSaved ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            onClick={handleOpen}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 600,
                                ml: isMobile ? 0 : 2,
                                mt: isMobile ? 0.25 : 0,
                                whiteSpace: 'nowrap',
                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                px: { xs: 1.25, sm: 2.5 },
                                py: { xs: 0.35, sm: 0.75 },
                            }}
                        >
                            Add to board
                        </Button>
                    )}
                </Stack>
            </CardContent>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMobile ? 'flex-start' : 'flex-end',
                    minWidth: { xs: '100%', sm: 140 },
                    pl: { xs: 0, sm: 3 },
                    justifyContent: 'center',
                    gap: { xs: 0.5, sm: 1 },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: -0.5,
                            fontSize: { xs: '1.1rem', sm: '1.5rem' },
                            mb: 0.25,
                        }}
                    >
                        {displayPrice}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        href={product.extractedProductLink || product.link || '#'}
                        target="_blank"
                        sx={{
                            borderRadius: 999,
                            fontWeight: 600,
                            minWidth: { xs: 'auto', sm: 110 },
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            boxShadow: 1,
                            textTransform: 'capitalize',
                            px: { xs: 2, sm: 3 },
                            py: { xs: 0.6, sm: 1 },
                        }}
                    >
                        View Deal
                    </Button>
                </Box>
            </Box>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Select Board</DialogTitle>
                <DialogContent>
                    <Select value={selectedBoard} onChange={handleBoardSelect} fullWidth displayEmpty sx={{ minWidth: 240 }}>
                        <MenuItem value="" disabled>Select a board</MenuItem>
                        {boards.map(b => (
                            <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>
                        ))}
                        <MenuItem value="__new__">+ Create new board</MenuItem>
                    </Select>
                    {creatingNew && (
                        <>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2">New Board Name</Typography>
                                <input
                                    type="text"
                                    value={newBoardName}
                                    onChange={e => setNewBoardName(e.target.value)}
                                    style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 4, border: '1px solid #ccc' }}
                                    placeholder="Enter board name"
                                />
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2">Description (optional)</Typography>
                                <input
                                    type="text"
                                    value={newBoardDesc}
                                    onChange={e => setNewBoardDesc(e.target.value)}
                                    style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 4, border: '1px solid #ccc' }}
                                    placeholder="Enter description"
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleAddToBoard} disabled={loading || (!selectedBoard && !creatingNew) || (creatingNew && !newBoardName.trim())}>
                        {loading ? <CircularProgress size={20} /> : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={!!feedback} autoHideDuration={6000} onClose={() => setFeedback('')}>
                <Alert onClose={() => setFeedback('')} severity={feedback.includes('successfully') ? 'success' : 'error'}>
                    {feedback}
                </Alert>
            </Snackbar>
        </Card>
    );
};

export default ProductCard; 